import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import "../Header.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { analyticsService } from "../services/analyticsService";
import { useTheme } from "../hooks/useTheme";
const LoginModal = lazy(() => import("./auth/LoginModal"));
const SignupModal = lazy(() => import("./auth/SignupModal"));
const SubscriptionModal = lazy(() => import("./subscription/SubscriptionModal"));
const CreditDisplay = lazy(() => import("./common/CreditDisplay"));
const UserDropdown = lazy(() => import("./common/UserDropdown"));

const NAV_ITEMS = [
  { name: "Home", id: "home", icon: "home" },
  { name: "Features", id: "features", icon: "auto_awesome" },
  { name: "Calculators", id: "calculators", icon: "calculate" },
  { name: "Blog", id: "blog", icon: "article", isRoute: true },
  { name: "Resources", id: "resources", icon: "library_books" },
  { name: "Pricing", id: "pricing", icon: "payments", isRoute: true },
];

const USER_MENU_ITEMS = (
  isAdmin: boolean
): { label: string; icon: string; color: string; route?: string; upgrade?: boolean }[] => {
  const items = [
    { label: "Dashboard", icon: "dashboard", color: "#6366f1", route: "/dashboard" },
    { label: "Profile", icon: "person", color: "#94a3b8", route: "/profile" },
    { label: "Upgrade Plan", icon: "workspace_premium", color: "#f59e0b", upgrade: true },
  ];
  if (isAdmin) {
    items.splice(1, 0, { label: "Admin Panel", icon: "admin_panel_settings", color: "#8b5cf6", route: "/admin" });
  }
  return items;
};

const Header: React.FC = () => {
  const { user, isAuthenticated, signOut, loading, initialized } = useAuthStore();
  const { toggle: toggleTheme, isDark } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeNav, setActiveNav] = useState("home");
  const isManualScrolling = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navItemsRef = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") return;
    const ids = NAV_ITEMS.filter(n => !n.isRoute).map(n => n.id);
    const observer = new IntersectionObserver(
      (entries) => {
        if (isManualScrolling.current) return;

        const visibleSections = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visibleSections.length > 0) {
          setActiveNav(visibleSections[0].target.id);
        }
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5],
        rootMargin: "-80px 0px -50% 0px"
      }
    );

    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      // Check if click was on ANY user menu button or dropdown
      const target = e.target as HTMLElement;
      const isMenuClick = target.closest('.user-menu-container') || target.closest('.user-menu-btn');

      if (!isMenuClick) {
        setUserMenu(false);
      }
    };
    if (userMenu) document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [userMenu]);

  useEffect(() => {
    if (mobileOpen) {
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${sbw}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const fn = () => window.location.reload();
    window.addEventListener("subscriptionUpdated", fn);
    return () => window.removeEventListener("subscriptionUpdated", fn);
  }, []);

  const scrollTo = (id: string) => {
    analyticsService.navigationClicked(id);
    const el = document.getElementById(id);
    if (el) {
      isManualScrolling.current = true;
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

      window.scrollTo({
        top: el.getBoundingClientRect().top + window.pageYOffset - 100,
        behavior: "smooth"
      });

      // Release lock after smooth scroll finishes (approx 800ms)
      scrollTimeout.current = setTimeout(() => {
        isManualScrolling.current = false;
      }, 800);
    }
  };

  const handleNav = (item: typeof NAV_ITEMS[0]) => {
    if (item.isRoute) {
      analyticsService.navigationClicked(item.id);
      setActiveNav(item.id);
      navigate(`/${item.id}`);
    }
    else {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          setActiveNav(item.id);
          scrollTo(item.id);
        }, 300);
      } else {
        setActiveNav(item.id);
        scrollTo(item.id);
      }
    }
    setMobileOpen(false);
  };

  const handleUserAction = (item: ReturnType<typeof USER_MENU_ITEMS>[0]) => {
    setUserMenu(false); setMobileOpen(false);
    if (item.upgrade) { setSubOpen(true); return; }
    if (item.route) { analyticsService.navigationClicked(item.label.toLowerCase()); navigate(item.route); }
  };

  const handleSignOut = async () => {
    setUserMenu(false); setMobileOpen(false);
    await signOut(); navigate("/");
  };

  const isHome = location.pathname === "/";

  // Sync activeNav with route on load/change
  useEffect(() => {
    const routeItem = NAV_ITEMS.find(n => n.isRoute && location.pathname.startsWith(`/${n.id}`));
    if (routeItem) {
      setActiveNav(routeItem.id);
    } else if (isHome && activeNav === "home") {
      // Keep home or let observer take over
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isHome]);

  // Update sliding indicator style
  useEffect(() => {
    const activeItem = navItemsRef.current[activeNav];
    if (activeItem) {
      setIndicatorStyle({
        left: activeItem.offsetLeft,
        width: activeItem.offsetWidth,
        opacity: 1
      });
    } else {
      setIndicatorStyle(prev => ({ ...prev, opacity: 0 }));
    }
  }, [activeNav, location.pathname]);

  // ── Logo ──────────────────────────────────────────────────
  const Logo = () => (
    <button
      onClick={() => {
        if (location.pathname !== "/") {
          navigate("/");
          setTimeout(() => {
            setActiveNav("home");
            scrollTo("home");
          }, 300);
        } else {
          setActiveNav("home");
          scrollTo("home");
        }
      }}
      aria-label="FinCalc Home"
      className="flex items-center gap-2 sm:gap-2.5 md:gap-3 focus:outline-none flex-shrink-0"
    >
      <div className="relative w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 lg:w-12 lg:h-12 rounded-xl sm:rounded-2xl overflow-hidden flex-shrink-0">
                      <img src="/logo.webp" alt="FinCalc" className="w-full h-full object-cover" draggable={false} width="48" height="48" />
      </div>
      <div className="flex flex-col leading-none">
        <span
          className="text-base sm:text-lg md:text-xl lg:text-[22px] font-black tracking-tight"
          style={{
            backgroundImage: isDark
              ? 'linear-gradient(to right, #ffffff, #e0e7ff, #a5b4fc)'
              : 'linear-gradient(to right, #4f46e5, #9333ea, #db2777)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            transition: 'opacity 0.3s',
          }}
        >
          FinCalc
        </span>
        <span className={`text-[8px] sm:text-[9px] md:text-[10px] font-bold tracking-widest uppercase hidden sm:block ${isDark ? 'text-indigo-400' : 'text-indigo-600'
          }`}>Smart Finance</span>
      </div>
    </button>
  );

  return (
    <>
      {/* ── HEADER ─────────────────────────────────────────── */}
      <header
        className={`fixed top-3 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] lg:w-[calc(100%-4rem)] max-w-7xl rounded-2xl will-change-transform
          ${scrolled
            ? "py-2 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-white/10 backdrop-blur-md"
            : "py-2.5 glass backdrop-blur-md"}
          hover:border-slate-300 dark:hover:border-white/20`}
        style={{ transition: 'transform 0.5s, opacity 0.5s' }}
      >
        <div className="px-3 sm:px-5 lg:px-6">

          {/* ── MOBILE / TABLET ROW (< lg) ── */}
          <div className="lg:hidden flex items-center justify-between gap-2">
            <Logo />

            {/* Right side actions */}
            <div className="flex items-center gap-1.5 sm:gap-2">

              {/* Search Icon */}
              <button
                onClick={() => {
                  navigate('/calculators');
                  setTimeout(() => {
                    const el = document.getElementById('calculators-search');
                    if (el) el.focus();
                  }, 150);
                }}
                aria-label="Search calculators"
                className={`header-icon-btn flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl hover:scale-110 active:scale-95 ${isDark
                  ? 'bg-slate-800 hover:bg-slate-700 border border-white/10 hover:border-white/20'
                  : 'bg-slate-100/90 hover:bg-indigo-100 border border-slate-200 hover:border-indigo-300'
                  }`}
                style={{ transition: 'transform 0.3s, opacity 0.3s' }}
              >
                <span className={`material-symbols-outlined text-[18px] sm:text-[20px] ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>search</span>
              </button>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className={`header-icon-btn flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl hover:scale-110 active:scale-95 ${isDark
                  ? 'bg-slate-800 hover:bg-slate-700 border border-white/10 hover:border-white/20'
                  : 'bg-slate-100/90 hover:bg-indigo-100 border border-slate-200 hover:border-indigo-300'
                  }`}
                style={{ transition: 'transform 0.3s, opacity 0.3s' }}
              >
                <span className={`material-symbols-outlined text-[18px] sm:text-[20px] transition-colors ${isDark ? 'text-amber-400' : 'text-indigo-600'
                  }`} style={{ fontVariationSettings: "'FILL' 1" }}>{isDark ? "light_mode" : "dark_mode"}</span>
              </button>

              {loading && !initialized ? (
                <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-white/5 animate-pulse" />
              ) : isAuthenticated && user ? (
                <>
                  {/* Credit pill — tablet only */}
                  <div className="hidden sm:flex">
                    <CreditDisplay onUpgradeClick={() => setSubOpen(true)} />
                  </div>

                  {/* Avatar */}
                  <div className="relative user-menu-container">
                    <button
                      onClick={() => setUserMenu(v => !v)}
                      aria-label="Open user menu"
                      className={`user-menu-btn relative w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center text-[13px] sm:text-sm font-black active:scale-95 overflow-hidden border ${isDark
                        ? 'bg-slate-800 border-white/10 text-indigo-100'
                        : 'bg-slate-100 border-slate-200 text-indigo-600'
                        }`}
                      style={{ transition: 'transform 0.3s, opacity 0.3s' }}
                    >
                      {user?.profile_picture_url ? (
                        <img
                          src={user.profile_picture_url}
                          alt={user.name || 'User'}
                          className="w-full h-full object-cover"
                          width="44"
                          height="44"
                        />
                      ) : (
                        user?.name?.charAt(0)?.toUpperCase() || 'U'
                      )}
                      <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-emerald-400 rounded-full border-2 animate-pulse ${isDark ? 'border-slate-900' : 'border-white'}`} />
                    </button>
                    <Suspense fallback={null}>
                      {userMenu && (
                        <UserDropdown
                          user={user}
                          items={USER_MENU_ITEMS(!!user.isAdmin)}
                          onAction={handleUserAction}
                          onSignOut={handleSignOut}
                          onUpgrade={() => setSubOpen(true)}
                          className="right-0"
                        />
                      )}
                    </Suspense>
                  </div>
                </>
              ) : (
                /* Sign up pill — tablet only */
                <button
                  onClick={() => setSignupOpen(true)}
                  className="hidden sm:flex btn-brand items-center gap-1.5 px-4 py-2 text-[12px] font-bold rounded-full"
                >
                  <span className="material-symbols-outlined text-[14px]">rocket_launch</span>
                  Get Started
                </button>
              )}

              {/* Hamburger */}
              <button
                onClick={() => setMobileOpen(v => !v)}
                aria-label="Toggle menu"
                className={`header-icon-btn flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl hover:scale-110 active:scale-95 ${isDark
                  ? 'bg-slate-800/90 hover:bg-slate-700/90 border border-white/20 hover:border-white/30'
                  : 'bg-slate-100/90 hover:bg-slate-200 border border-slate-200 hover:border-slate-300'
                  }`}
                style={{ transition: 'transform 0.3s, opacity 0.3s' }}
              >
                <span className={`material-symbols-outlined text-[20px] sm:text-[22px] ${mobileOpen ? "rotate-90 text-indigo-500" : "rotate-0 " + (isDark ? 'text-slate-300' : 'text-slate-700')
                  }`} style={{ transition: 'transform 0.5s' }}>{mobileOpen ? "close" : "menu"}</span>
              </button>
            </div>
          </div>

          {/* ── DESKTOP ROW (≥ lg) ── */}
          <div className="hidden lg:flex items-center justify-between gap-2 xl:gap-6">
            <Logo />

            {/* Nav pill */}
            <nav className="relative flex items-center bg-slate-100/50 dark:bg-white/5 rounded-full px-1 lg:px-1.5 py-1 lg:py-1.5 border border-slate-200/50 dark:border-white/10 gap-0.5 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* Sliding Indicator Background */}
              <div
                className="absolute h-[calc(100%-12px)] bg-white dark:bg-slate-700/90 rounded-full border border-slate-200/60 dark:border-white/10 pointer-events-none z-0"
                style={{
                  left: `${indicatorStyle.left}px`,
                  width: `${indicatorStyle.width}px`,
                  opacity: indicatorStyle.opacity,
                  top: '6px',
                  transition: 'left 0.5s cubic-bezier(0.23,1,0.32,1), width 0.5s cubic-bezier(0.23,1,0.32,1)'
                }}
              />

              {NAV_ITEMS.map(item => {
                const isActive = (isHome && !item.isRoute && activeNav === item.id) ||
                  (item.isRoute && location.pathname.startsWith(`/${item.id}`));

                return (
                  <button
                    key={item.id}
                    ref={el => navItemsRef.current[item.id] = el}
                    onClick={() => handleNav(item)}
                    aria-label={`Go to ${item.name}`}
                    className={`relative px-2.5 lg:px-3 xl:px-4 py-1.5 lg:py-2 rounded-full text-[11px] lg:text-[12.5px] xl:text-[13px] font-black active:scale-95 z-10
                      ${isActive
                        ? "text-indigo-600 dark:text-white"
                        : "text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-white"}`}
                    style={{ transition: 'opacity 0.3s' }}
                  >
                    <span className="relative z-10">{item.name}</span>
                    {isActive && (
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-indigo-500 dark:bg-indigo-400 rounded-full" style={{ transition: 'opacity 0.5s' }} />
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-1.5 lg:gap-2 flex-shrink min-w-0">
              {/* Search Icon */}
              <button
                onClick={() => {
                  navigate('/calculators');
                  setTimeout(() => {
                    const el = document.getElementById('calculators-search');
                    if (el) el.focus();
                  }, 150);
                }}
                aria-label="Search calculators"
                className={`header-icon-btn flex items-center justify-center w-9 lg:w-10 xl:w-11 h-9 lg:h-10 xl:h-11 rounded-xl xl:rounded-2xl hover:scale-110 active:scale-95 ${isDark
                  ? 'bg-slate-800/90 hover:bg-indigo-500/20 border border-white/20 hover:border-indigo-500/30'
                  : 'bg-slate-100/90 hover:bg-indigo-100 border border-slate-200 hover:border-indigo-300'
                  }`}
                style={{ transition: 'transform 0.3s, opacity 0.3s' }}
              >
                <span className={`material-symbols-outlined text-[17px] lg:text-[19px] xl:text-[21px] ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>search</span>
              </button>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className={`header-icon-btn flex items-center justify-center w-9 lg:w-10 xl:w-11 h-9 lg:h-10 xl:h-11 rounded-xl xl:rounded-2xl hover:scale-110 active:scale-95 ${isDark
                  ? 'bg-slate-800/90 hover:bg-amber-500/20 border border-white/20 hover:border-amber-500/30'
                  : 'bg-slate-100/90 hover:bg-indigo-100 border border-slate-200 hover:border-indigo-300'
                  }`}
                style={{ transition: 'transform 0.3s, opacity 0.3s' }}
              >
                <span className={`material-symbols-outlined text-[17px] lg:text-[19px] xl:text-[21px] hover:rotate-180 ${isDark ? 'text-amber-400' : 'text-indigo-600'
                  }`} style={{ fontVariationSettings: "'FILL' 1", transition: 'transform 0.3s' }}>{isDark ? "light_mode" : "dark_mode"}</span>
              </button>

              {loading && !initialized ? (
                <div className="w-32 h-10 rounded-full bg-slate-200 dark:bg-white/5 animate-pulse" />
              ) : isAuthenticated && user ? (
                <div className="relative user-menu-container">
                  <div
                    onClick={() => setUserMenu(v => !v)}
                    aria-label="Open user menu"
                    role="button"
                    tabIndex={0}
                    className={`user-menu-btn cursor-pointer flex items-center gap-2.5 pl-3 pr-2 py-1.5 rounded-full border ${isDark
                      ? 'bg-slate-800/90 border-white/20 hover:bg-slate-700/90 hover:border-white/30'
                      : 'bg-slate-100/90 border-slate-200 hover:bg-white hover:border-slate-300'
                      }`}
                    style={{ transition: 'opacity 0.3s' }}
                  >
                    {/* Credit pill inside button */}
                    <div className="hidden xl:flex">
                      <CreditDisplay onUpgradeClick={() => setSubOpen(true)} />
                    </div>
                    <span className={`text-[12.5px] font-bold max-w-[100px] truncate hidden xl:block ${isDark ? 'text-slate-300' : 'text-slate-700'
                      }`}>{user.name.split(" ")[0]}</span>
                    <div className={`relative w-8 h-8 md:w-8 lg:w-9 h-8 md:h-8 lg:h-9 rounded-full flex items-center justify-center text-[11px] lg:text-[12px] font-black group-hover/avatar:scale-105 overflow-hidden border ${isDark
                      ? 'bg-slate-800 border-white/10 text-white'
                      : 'bg-slate-100 border-slate-200 text-white'
                      }`}
                    style={{ transition: 'transform 0.3s' }}>
                      {user?.profile_picture_url ? (
                        <img
                          src={user.profile_picture_url}
                          alt={user.name || 'User'}
                          className="w-full h-full object-cover"
                          width="36"
                          height="36"
                        />
                      ) : (
                        user?.name?.charAt(0)?.toUpperCase() || 'U'
                      )}
                      <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 animate-pulse ${isDark ? 'border-slate-900' : 'border-white'
                        }`} />
                    </div>
                  </div>
                  <Suspense fallback={<div className="w-10 h-10 rounded-full bg-white/5 animate-pulse" />}>
                    {userMenu && (
                      <UserDropdown
                        user={user}
                        items={USER_MENU_ITEMS(!!user.isAdmin)}
                        onAction={handleUserAction}
                        onSignOut={handleSignOut}
                        onUpgrade={() => setSubOpen(true)}
                        className="right-0"
                        wide
                      />
                    )}
                  </Suspense>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {/* Anonymous credit pill */}
                  <div className="hidden sm:flex">
                    <CreditDisplay onUpgradeClick={() => setSignupOpen(true)} />
                  </div>
                  <button
                    onClick={() => setLoginOpen(true)}
                    aria-label="Log in to your account"
                    className={`header-login-btn px-3 lg:px-5 py-1.5 lg:py-2.5 rounded-xl text-[11px] lg:text-[13px] font-bold hover:scale-105 active:scale-95 ${isDark
                      ? 'bg-slate-800/90 hover:bg-slate-700/90 text-slate-300 hover:text-white border border-white/10 hover:border-white/20'
                      : 'bg-slate-100/90 hover:bg-white text-slate-600 hover:text-indigo-600 border border-slate-200 hover:border-slate-300'
                      }`}
                    style={{ transition: 'transform 0.3s, opacity 0.3s' }}
                  >
                    Log In
                  </button>
                  <button
                    onClick={() => setSignupOpen(true)}
                    className="btn-brand relative flex items-center gap-1 lg:gap-2 px-3 lg:px-5 py-1.5 lg:py-2.5 rounded-xl text-[11px] lg:text-[13px] font-black overflow-hidden group hover:scale-105 active:scale-95"
                    style={{ transition: 'transform 0.3s, opacity 0.3s' }}
                  >
                    <span className="relative z-10">Get Started</span>
                    <span className="material-symbols-outlined text-[13px] lg:text-[15px] relative z-10 group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </header>

      {/* ── MOBILE DRAWER ──────────────────────────────────── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 backdrop-blur-md animate-fade-in ${isDark ? 'bg-black/70' : 'bg-slate-900/40'}`}
            onClick={() => setMobileOpen(false)}
          />

          {/* Drawer panel */}
          <div
            className="absolute top-0 right-0 h-full w-[280px] sm:w-[320px] flex flex-col animate-slide-in-right overflow-hidden"
            style={isDark ? {
              background: 'linear-gradient(170deg, #0f172a 0%, #111827 50%, #000000 100%)',
              borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
            } : {
              background: 'linear-gradient(170deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)',
              borderLeft: '1px solid rgba(0, 0, 0, 0.05)',
            }}
          >
            {/* ── Header ── */}
            <div
              className="relative flex items-center justify-between px-5 py-5 flex-shrink-0"
              style={isDark ? {
                background: 'linear-gradient(135deg, rgba(255,255,255,0.02), transparent)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              } : {
                background: 'linear-gradient(135deg, rgba(0,0,0,0.01), transparent)',
                borderBottom: '1px solid rgba(0,0,0,0.04)',
              }}
            >
              <Logo />
              <button
                onClick={() => setMobileOpen(false)}
                className={`w-8 h-8 flex items-center justify-center rounded-full active:scale-90 ${isDark ? 'bg-white/5 text-slate-400 hover:text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-900'
                  }`}
                style={{ transition: 'transform 0.3s, opacity 0.3s' }}
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>

            {/* ── Scrollable body ── */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">

              {/* ── User profile card ── */}
              {loading && !initialized ? (
                <div className="px-4 pt-5 pb-3">
                  <div className="h-32 w-full rounded-2xl bg-slate-200 dark:bg-white/5 animate-pulse" />
                </div>
              ) : isAuthenticated && user ? (
                <div className="px-4 pt-5 pb-3">
                  <div
                    className="rounded-2xl p-4"
                    style={isDark ? {
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    } : {
                      background: 'rgba(255, 255, 255, 0.5)',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white flex items-center justify-center text-base font-black overflow-hidden">
                          {user?.profile_picture_url ? (
                            <img src={user.profile_picture_url} alt={user.name} className="w-full h-full object-cover" width="48" height="48" />
                          ) : (
                            user.name.charAt(0).toUpperCase()
                          )}
                        </div>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 ${isDark ? 'border-slate-900' : 'border-white'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-black truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
                        <p className={`text-[11px] font-medium truncate mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{user.email}</p>
                      </div>
                    </div>
                    <div
                      className="rounded-2xl px-3 py-2.5"
                      style={isDark ? { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' } : { background: '#ffffff', border: '1px solid rgba(0,0,0,0.05)' }}
                    >
                      <CreditDisplay onUpgradeClick={() => { setMobileOpen(false); setSubOpen(true); }} className="w-full justify-center" />
                    </div>
                  </div>
                </div>
              ) : (
                /* Guest CTA */
                <div className="px-4 pt-4 pb-2">
                  <div
                    className="rounded-3xl p-5 text-center"
                    style={isDark ? {
                      background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.07))',
                      border: '1px solid rgba(99,102,241,0.2)',
                    } : {
                      background: 'linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.03))',
                      border: '1px solid rgba(99,102,241,0.12)',
                    }}
                  >
                    <div className="w-12 h-12 mx-auto mb-3 rounded-2xl overflow-hidden">
                      <img src="/logo.webp" alt="FinCalc" className="w-full h-full object-cover" draggable={false} width="48" height="48" />
                    </div>
                    <p className={`text-base font-black mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Start for Free</p>
                    <p className={`text-[12px] mb-4 font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>3 free calculations, no signup needed</p>
                    <div className="flex gap-2.5">
                      <button onClick={() => { setMobileOpen(false); setLoginOpen(true); }} className={`flex-1 py-3 rounded-xl text-[13px] font-bold active:scale-95 ${isDark ? 'bg-white/5 hover:bg-white/10 text-slate-300 border border-white/5' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'}`} style={{ transition: 'transform 0.3s, opacity 0.3s' }}>Log In</button>
                      <button onClick={() => { setMobileOpen(false); setSignupOpen(true); }} className="flex-1 py-3 rounded-xl text-[13px] font-black btn-brand active:scale-95" style={{ transition: 'transform 0.3s, opacity 0.3s' }}>Get Started</button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Search section ── */}
              <div className="px-4 pt-2 pb-2">
                <form 
                  onSubmit={(e) => {
                    e.preventDefault();
                    const target = e.currentTarget.elements.namedItem('q') as HTMLInputElement;
                    if (target?.value.trim()) {
                      navigate(`/calculators?q=${encodeURIComponent(target.value.trim())}`);
                      setMobileOpen(false);
                    }
                  }}
                  action="/calculators"
                  method="GET"
                  className="relative flex items-center"
                  role="search"
                >
                  <span className="material-symbols-outlined absolute left-3 text-slate-400 text-[18px]">search</span>
                  <input
                    type="search"
                    name="q"
                    placeholder="Search calculators..."
                    className={`w-full pl-9 pr-3 py-2 rounded-xl text-xs font-semibold outline-none border ${
                      isDark 
                        ? 'bg-slate-800/80 border-white/10 text-white placeholder-slate-500 focus:border-indigo-500/50' 
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-300'
                    }`}
                    style={{ transition: 'opacity 0.3s' }}
                  />
                </form>
              </div>

              {/* ── Navigation section ── */}
              <div className="px-3 pt-3 pb-2">
                <div className="flex items-center gap-2 px-3 mb-3">
                  <span className={`text-[10px] font-black uppercase tracking-[0.25em] ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>Explore</span>
                  <div className={`flex-1 h-[1px] ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                </div>
                <div className="space-y-1.5 px-1">
                  {/* Theme toggle */}
                  <button
                    onClick={toggleTheme}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-[14px] font-bold active:scale-95 group ${isDark ? 'text-slate-300 hover:text-white hover:bg-white/8' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    style={{ transition: 'transform 0.3s, opacity 0.3s' }}
                  >
                    <span className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? 'bg-amber-500/15 group-hover:bg-amber-500/25' : 'bg-amber-50 group-hover:bg-amber-100'
                      }`}
                      style={{ transition: 'opacity 0.3s' }}>
                      <span className="material-symbols-outlined text-[18px] text-amber-500" style={{ fontVariationSettings: "'FILL' 1" }}>
                        {isDark ? 'light_mode' : 'dark_mode'}
                      </span>
                    </span>
                    <span className="flex-1 text-left">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                    <div className={`flex items-center px-2.5 py-1 rounded-lg ${isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-50 text-amber-600'
                      }`}
                      style={{ transition: 'opacity 0.3s' }}>
                      <span className="text-[10px] font-black tracking-widest">{isDark ? 'ON' : 'OFF'}</span>
                    </div>
                  </button>

                  {NAV_ITEMS.map((item, i) => {
                    const isActive = (isHome && !item.isRoute && activeNav === item.id) ||
                      (item.isRoute && location.pathname.startsWith(`/${item.id}`));
                    return (
                      <button
                        key={item.name}
                        onClick={() => handleNav(item)}
                        style={{ animationDelay: `${i * 40}ms`, transition: 'transform 0.3s, opacity 0.3s' }}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-[14px] font-bold active:scale-95 group ${isActive
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                          : isDark
                            ? 'text-slate-300 hover:text-white hover:bg-white/8'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                          }`}
                      >
                        <span className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${isActive
                          ? 'bg-white/20'
                          : isDark ? 'bg-indigo-500/15 group-hover:bg-indigo-500/25' : 'bg-indigo-50 group-hover:bg-indigo-100'
                          }`}
                          style={{ transition: 'opacity 0.3s' }}>
                          <span className={`material-symbols-outlined text-[17px] ${isActive ? 'text-white' : isDark ? 'text-indigo-400' : 'text-indigo-600'
                            }`} style={{ fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0" }}>
                            {item.icon}
                          </span>
                        </span>
                        <span className="flex-1 text-left">{item.name}</span>
                        {isActive
                          ? <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
                          : <span className={`material-symbols-outlined text-[14px] opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-slate-500' : 'text-slate-400'
                            }`}>chevron_right</span>
                        }
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Account section (authenticated) ── */}
              {isAuthenticated && user && (
                <div className="px-3 pt-4 pb-2">
                  <div className="flex items-center gap-2 px-2 mb-2.5">
                    <span className={`text-[9px] font-black uppercase tracking-[0.2em] ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>Account</span>
                    <div className={`flex-1 h-[1px] ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                  </div>
                  <div className="space-y-1">
                    {USER_MENU_ITEMS(!!user.isAdmin).map(item => (
                      <button
                        key={item.label}
                        onClick={() => handleUserAction(item)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold active:scale-95 group ${isDark ? 'text-slate-300 hover:text-white hover:bg-white/8' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                          }`}
                        style={{ transition: 'transform 0.3s, opacity 0.3s' }}
                      >
                        <span className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${isDark ? 'bg-white/6 group-hover:bg-white/12' : 'bg-slate-100 group-hover:bg-slate-200'
                          }`}
                          style={{ transition: 'opacity 0.3s' }}>
                          <span className="material-symbols-outlined text-[16px] group-hover:scale-110 transition-transform" style={{ color: item.color, fontVariationSettings: "'FILL' 1" }}>{item.icon}</span>
                        </span>
                        <span className="flex-1 text-left">{item.label}</span>
                        <span className={`material-symbols-outlined text-[14px] opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-slate-500' : 'text-slate-400'
                          }`}>chevron_right</span>
                      </button>
                    ))}

                    {/* Divider */}
                    <div className={`my-1 h-px mx-1 ${isDark ? 'bg-white/8' : 'bg-slate-200'}`} />

                    <button
                      onClick={handleSignOut}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold active:scale-95 group ${isDark ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10' : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                        }`}
                      style={{ transition: 'transform 0.3s, opacity 0.3s' }}
                    >
                      <span className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${isDark ? 'bg-red-500/12 group-hover:bg-red-500/20' : 'bg-red-50 group-hover:bg-red-100'
                        }`}
                        style={{ transition: 'opacity 0.3s' }}>
                        <span className="material-symbols-outlined text-[16px] group-hover:scale-110 transition-transform" style={{ fontVariationSettings: "'FILL' 1" }}>logout</span>
                      </span>
                      <span className="flex-1 text-left">Sign Out</span>
                    </button>
                  </div>
                </div>
              )}

              {/* bottom padding */}
              <div className="h-6" />
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE BOTTOM BAR (guests only) ─────────────────── */}
      {!isAuthenticated && (
        <div
          className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 lg:hidden w-[90%] max-w-xs px-3 py-2.5 glass-strong rounded-[20px] transform-gpu ${isDark ? 'border-white/10' : 'border-slate-200'}`}
          style={{ minHeight: '56px', isolation: 'isolate', background: isDark ? undefined : '#ffffff', transition: 'transform 0.3s, opacity 0.3s' }}
        >
          <div className="flex items-center gap-2 h-full">
            <button
              onClick={() => { setMobileOpen(false); setLoginOpen(true); }}
              className={`flex-1 py-2.5 rounded-xl border text-[13px] font-bold active:scale-95 ${isDark
                ? 'border-white/10 text-slate-200 hover:bg-white/10'
                : 'border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100'
                }`}
              style={{ transition: 'transform 0.3s, opacity 0.3s' }}
            >
              Log In
            </button>
            <button
              onClick={() => setSignupOpen(true)}
              className="flex-[1.6] btn-brand relative flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[13px] font-black overflow-hidden group active:scale-95"
              style={{ transition: 'transform 0.3s, opacity 0.3s' }}
            >
              <span className="z-10">Get Started</span>
              <span className="material-symbols-outlined text-[15px] z-10 group-hover:translate-x-0.5 transition-transform">arrow_forward</span>
            </button>
          </div>
        </div>
      )}

      {/* ── MODALS ─────────────────────────────────────────── */}
      <Suspense fallback={null}>
        {loginOpen && <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} onSwitchToSignup={() => { setLoginOpen(false); setSignupOpen(true); }} />}
        {signupOpen && <SignupModal isOpen={signupOpen} onClose={() => setSignupOpen(false)} onSwitchToLogin={() => { setSignupOpen(false); setLoginOpen(true); }} />}
        {subOpen && <SubscriptionModal isOpen={subOpen} onClose={() => setSubOpen(false)} />}
      </Suspense>
    </>
  );
};

export default Header;
